import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { IdName } from 'interfaces/IdName';
import { IMarket } from 'interfaces/IMarket';
import {
  I18N_AVANT_PROPERTY_COMMON_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
} from 'constants/i18n';
import { PropertyTypeNames, PropertyTypes } from 'constants/propertyTypes';
import { PropertySubtypes } from 'constants/propertySubtypes';
import { PropertyClass } from 'constants/propertyClass';
import { BUILDING_CLASSES_OPTIONS } from 'constants/buildingClasses';
import { FindCompsSearchTab } from 'constants/findCompTabs';
import { authContext, useUserAccessData } from 'contexts/AuthContext';
import { FindCompsActions, useFindCompsData } from 'contexts/FindCompsContext';
import { useUserMarkets } from 'hooks/useUserMarkets';
import { translateText } from 'utils/i18n';
import { deepCopy } from 'utils/objects';

import { MenuDropdown } from './Components/MenuDropdown';
import {
  getCompPropertiesLinks,
  getMappingLinks,
  getMarketAnalyticsLinks,
  getSubmarketList,
  getTopFiveMarketList,
} from './utils';
import styles from './SubNavBar.module.scss';
import { useNavBarData } from './useNavBarData';

const SubNavBar = () => {
  const { user } = useContext(authContext);
  const history = useHistory();

  const { dispatch: findCompsAction, clearSmartSearch } = useFindCompsData();

  const { fullAccess, accessData } = useUserAccessData();

  const [userPreferredType, setUserPreferredType] = useState<IdName | null>(
    null,
  );
  const [initialListMarket, setInitialListMarket] = useState<IMarket[]>([]);
  const [currentMarket, setCurrentMarket] = useState<IMarket>();

  const { userMarkets } = useUserMarkets(user, true);

  useEffect(() => {
    if (!userMarkets.length) return;

    const listMarkets = getTopFiveMarketList(deepCopy(userMarkets)) ?? [];
    setInitialListMarket(listMarkets);
    setCurrentMarket(listMarkets[0]);
  }, [userMarkets]);

  const isIndustrialFlex = [
    PropertyTypes.flexIndustrial,
    PropertyTypes.industrial,
  ].includes(userPreferredType?.name);

  const { data, handlers, loading } = useNavBarData({
    isIndustrialFlex,
    userMarkets,
    currentMarket,
    setCurrentMarket,
  });

  useEffect(() => {
    if (!data.propertyTypes.length || !user.preferences?.propertyType) return;

    const preferredType = data.propertyTypes.find(
      type => PropertyTypeNames[type?.id] === user.preferences?.propertyType,
    );
    if (preferredType?.id) {
      setUserPreferredType(preferredType);
    }
  }, [data.propertyTypes, user.preferences?.propertyType]);

  const onLinkClick = () => {
    clearSmartSearch();

    findCompsAction({
      type: FindCompsActions.set_current_search_tab,
      currentSearchTab: FindCompsSearchTab.MARKET_SEARCH,
    });
  };

  const propertyClasses = useMemo(() => {
    if (isIndustrialFlex) return [];

    return (
      data.classes
        .filter(c => BUILDING_CLASSES_OPTIONS.includes(c.name))
        .map(cn =>
          cn.name !== PropertyClass.Trophy
            ? { ...cn, name: `Class ${cn.name}` }
            : cn,
        ) ?? []
    );
  }, [data.classes, isIndustrialFlex]);

  const subTypes = useMemo(() => {
    if (!isIndustrialFlex) return [];

    return (
      data.subTypes.filter(subType => {
        if (subType.name === PropertySubtypes.filmStudio) return;

        const transformedType = userPreferredType?.name
          ?.replaceAll(' ', '')
          .toUpperCase();
        return subType.type === transformedType;
      }) ?? []
    );
  }, [data.subTypes, isIndustrialFlex, userPreferredType?.name]);

  const analyticsDashboardsId = accessData?.analyticsDashboardsId || [];

  const renderMarketMenuHeader = () => {
    return (
      <div className={styles['market-dropdown-content']}>
        {initialListMarket.map(market => (
          <button
            key={market.id}
            className={classNames(styles['market-dropdown-button'], {
              [styles['selected']]: data.currentMarket === market,
            })}
            onMouseOver={() => handlers.handleHoverMarket(market)}
            onClick={() => {
              handlers.handleClickMarket(market);
            }}
          >
            {market.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className={styles['menu-container']}>
      <div className={classNames(styles['menu-item'], styles['activity'])}>
        <Link to={handlers.onNewsClickHandle} className={styles['menu-link']}>
          {translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.news`)}
        </Link>
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-market-overview"
          label={translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.market_plural`,
          )}
          gridClassName={styles['market-dropdown-container']}
          listItems={getSubmarketList(data.submarkets, history)}
          customHeader={renderMarketMenuHeader()}
          isLoading={loading.submarketsDataLoading}
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-comps-dropdown"
          label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.data`)}
          listItems={getCompPropertiesLinks(
            user,
            userMarkets,
            userPreferredType,
            subTypes,
            propertyClasses,
            history,
            onLinkClick,
          )}
          isLoading={
            loading.propertyTypesDataLoading ||
            loading.subTypesDataLoading ||
            loading.classesDataLoading
          }
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-market-dropdown"
          label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.analytics`)}
          listItems={getMarketAnalyticsLinks(
            history,
            data.analytics,
            analyticsDashboardsId,
            fullAccess,
          )}
          gridClassName={styles['market-analytics-dropdown']}
          isLoading={loading.dynamicUrlsDataLoading}
        />
      </div>
      <div className={styles['menu-item']}>
        <MenuDropdown
          id="subnav-mapping"
          label={translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.products`,
          )}
          listItems={getMappingLinks()}
          gridClassName={styles['mapping-dropdown']}
        />
      </div>
    </div>
  );
};

export default SubNavBar;
